import React from 'react';
import { Label } from '@/components/ui/label';
import { FORME_IMAGES, FORMES_EMBOUT } from './orderFormConstants';

/**
 * Visual selector for an embout shape (forme). Renders a responsive grid of
 * clickable image tiles instead of a Select dropdown.
 *
 * Props:
 * - value: current selected forme value (e.g. 'canule')
 * - onChange: (newValue) => void
 * - options: optional override list [{value, label}]
 * - images: optional override mapping {value: imgPath} (e.g. FORME_IMAGES_RIC)
 * - label, required, testIdPrefix, changedBorderClass
 */
export default function FormeImageSelector({
  value,
  onChange,
  options,
  images,
  label,
  required,
  testIdPrefix = 'forme-tile-',
  changedBorderClass = '',
}) {
  const list = options && options.length ? options : FORMES_EMBOUT;
  const imgMap = images || FORME_IMAGES;

  return (
    <div data-testid="forme-image-selector">
      {label && (
        <Label className={`label-overline mb-2 block ${changedBorderClass ? 'text-red-600 font-bold' : ''}`}>
          {label}{required ? ' *' : ''}
        </Label>
      )}
      <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 ${changedBorderClass}`}>
        {list.map((opt) => {
          const active = value === opt.value;
          const img = imgMap[opt.value];
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`relative flex flex-col items-center p-2 rounded-sm border-2 transition-all bg-white ${active ? 'border-zinc-900 ring-2 ring-zinc-900/10 shadow-sm' : 'border-zinc-200 hover:border-zinc-400'}`}
              data-testid={`${testIdPrefix}${opt.value}`}
              aria-pressed={active}>
              <div className="w-full aspect-square bg-zinc-50 rounded-sm overflow-hidden flex items-center justify-center mb-1.5">
                {img ? (
                  <img src={img} alt={opt.label} className="w-full h-full object-contain" loading="lazy" />
                ) : (
                  <span className="text-xs text-zinc-400 px-2 text-center">{opt.label}</span>
                )}
              </div>
              <p className={`text-[11px] sm:text-xs text-center leading-tight ${active ? 'font-semibold text-zinc-900' : 'text-zinc-700'}`}>
                {opt.label}
              </p>
              {active && (
                <span className="absolute top-1 right-1 bg-zinc-900 text-white text-[10px] font-bold rounded-sm px-1.5 py-0.5">✓</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
